/**
 * Managed Agents — pure helpers for the per-brand Agents page. No React / no
 * I/O so they are directly unit-testable (see agent-helpers.test.ts).
 *
 * Status vocabulary + chip tones follow MANAGED_AGENTS_CONTRACT §4 verbatim:
 * runs move scheduled → running → generating → ready | failed | budget_reached
 * with tones neutral / warning(animated) / success / danger / danger.
 */
import type {
  AgentCadence,
  AgentFindingsStatus,
  AgentRunArtifact,
  AgentRunStatus,
  AgentRunTrigger,
  AgentView,
  RunView,
} from '../../../lib/api/client-reports/client-agents-api';

/**
 * The translator the label helpers take. It has the shape of react-i18next's
 * `t(key, defaultValue, options)`; components pass their own `t`. Left out,
 * the helpers answer in English ({@link englishT}), which keeps them usable
 * without an i18n instance.
 */
export type Translate = (
  key: string,
  defaultValue: string,
  options?: Record<string, unknown>
) => string;

/** English fallback: the default text with its `{{placeholders}}` filled in. */
export const englishT: Translate = (_key, defaultValue, options) =>
  defaultValue.replace(/\{\{\s*(\w+)\s*\}\}/g, (_m, name) =>
    String(options?.[name] ?? '')
  );

/** Chip tone shared by the run-status and agent-state pills. */
export type AgentTone = 'success' | 'warning' | 'danger' | 'neutral';

// ---------- run status ----------

/** Human label per run status; colours live in `.client-agents__chip--*`. */
export const AGENT_RUN_STATUS_LABEL: Record<AgentRunStatus, string> = {
  scheduled: 'Scheduled',
  running: 'Running',
  generating: 'Generating',
  ready: 'Ready',
  failed: 'Failed',
  budget_reached: 'Budget reached',
};

/** Statuses that are still moving — the page polls while any run is here. */
export const IN_FLIGHT_RUN_STATUSES: AgentRunStatus[] = [
  'running',
  'generating',
];

/** CONTRACT §4: scheduled=neutral, running/generating=warning (animated),
 *  ready=success, failed=danger, budget_reached=danger. */
export function agentRunTone(status: AgentRunStatus | string): AgentTone {
  switch (status) {
    case 'ready':
      return 'success';
    case 'running':
    case 'generating':
      return 'warning';
    case 'failed':
    case 'budget_reached':
      return 'danger';
    case 'scheduled':
      return 'neutral';
    default:
      return 'neutral';
  }
}

export function agentRunStatusLabel(
  status: AgentRunStatus | string,
  t: Translate = englishT
): string {
  switch (status) {
    case 'scheduled':
      return t('AgentRunStatusScheduled', 'Scheduled');
    case 'running':
      return t('AgentRunStatusRunning', 'Running');
    case 'generating':
      return t('AgentRunStatusGenerating', 'Generating');
    case 'ready':
      return t('AgentRunStatusReady', 'Ready');
    case 'failed':
      return t('AgentRunStatusFailed', 'Failed');
    case 'budget_reached':
      return t('AgentRunStatusBudgetReached', 'Budget reached');
    default:
      return String(status);
  }
}

/**
 * The run-history chip label. Same vocabulary as {@link agentRunStatusLabel}
 * except `generating`, which reads "Generating report…" in the table — that
 * row is the one thing moving on screen, so it says what is being made.
 */
export function runHistoryStatusLabel(
  status: AgentRunStatus | string,
  t: Translate = englishT
): string {
  if (status === 'generating') {
    return t('AgentRunStatusGeneratingReport', 'Generating report…');
  }
  return agentRunStatusLabel(status, t);
}

/** 'schedule' | 'manual' → column label. Unknown values echo through. */
export function triggerLabel(
  trigger: AgentRunTrigger | string,
  t: Translate = englishT
): string {
  if (trigger === 'schedule') return t('AgentRunTriggerScheduled', 'Scheduled');
  if (trigger === 'manual') return t('AgentRunTriggerManual', 'Manual');
  return String(trigger);
}

export function isRunInFlight(status: AgentRunStatus | string): boolean {
  return IN_FLIGHT_RUN_STATUSES.includes(status as AgentRunStatus);
}

/**
 * Should the page keep polling GET agent-runs? Only while at least one run is
 * running/generating (10s cadence per the contract). An empty list never
 * spins, and a non-array never throws.
 */
export function shouldPollRuns(runs: RunView[]): boolean {
  if (!Array.isArray(runs) || runs.length === 0) return false;
  return runs.some((r) => isRunInFlight(r.status));
}

/**
 * Fold a freshly polled FIRST page (newest 20, newest first) into the rows
 * already on screen without dropping pages the user loaded via "Load more":
 * fresh rows win (their statuses are the live ones), older loaded rows are
 * kept below. Safe because anything outside the newest page is settled —
 * running/generating runs are by definition recent.
 */
export function mergeRunPages(
  fresh: RunView[],
  existing: RunView[]
): RunView[] {
  const freshList = Array.isArray(fresh) ? fresh : [];
  const seen = new Set(freshList.map((r) => r.id));
  const kept = (Array.isArray(existing) ? existing : []).filter(
    (r) => !seen.has(r.id)
  );
  return [...freshList, ...kept];
}

// ---------- artifact downloads ----------

/**
 * The artifacts a run-history row may offer as download buttons:
 *
 *  - only once the run is `ready` — before that the files are not stored on
 *    our side yet, so a `generating` (or failed) row with artifact metadata
 *    must never render dead buttons;
 *  - only the `pptx`/`xlsx` kinds — the HTML half is ingested as a client
 *    report and opens through the workbench link, never as a download.
 *
 * Tolerates a non-array and unknown wire kinds (e.g. an `html` entry leaking
 * into `artifacts`) by dropping them.
 */
export function downloadableArtifacts(
  run: Pick<RunView, 'status' | 'artifacts'>
): AgentRunArtifact[] {
  if (run.status !== 'ready') return [];
  const list = Array.isArray(run.artifacts) ? run.artifacts : [];
  return list.filter((a) => a?.kind === 'pptx' || a?.kind === 'xlsx');
}

/**
 * The stored HTML scorecard of a ready findings-mode run (opened inline in the
 * sandboxed viewer), or null. Mirrors {@link downloadableArtifacts}' rules:
 * only a `ready` run, only a stored html entry.
 */
export function htmlOutputArtifact(
  run: Pick<RunView, 'status' | 'artifacts'>
): AgentRunArtifact | null {
  if (run.status !== 'ready') return null;
  const list = Array.isArray(run.artifacts) ? run.artifacts : [];
  return list.find((a) => a?.kind === 'html') ?? null;
}

// ---------- findings (Agents v3, D11) ----------

export interface FindingsChip {
  label: string;
  tone: AgentTone;
}

/**
 * The one-glance chip for a READY findings-mode run — the demo's vocabulary:
 * `ok → Completed`, `findings → Findings`, `needs_setup → Needs KPI targets`,
 * `failed → Failed`. Null for runs without findings (client-report /
 * workbook runs keep the plain status chip).
 */
export function findingsChip(
  run: Pick<RunView, 'status' | 'findings_status'> | null | undefined,
  t: Translate = englishT
): FindingsChip | null {
  if (!run || run.status !== 'ready') return null;
  const status = run.findings_status as AgentFindingsStatus | null | undefined;
  switch (status) {
    case 'ok':
      return {
        label: t('AgentFindingsCompleted', 'Completed'),
        tone: 'success',
      };
    case 'findings':
      return { label: t('AgentFindingsFindings', 'Findings'), tone: 'warning' };
    case 'needs_setup':
      return {
        label: t('AgentFindingsNeedsKpiTargets', 'Needs KPI targets'),
        tone: 'warning',
      };
    case 'failed':
      return { label: t('AgentRunStatusFailed', 'Failed'), tone: 'danger' };
    default:
      return null;
  }
}

/** The bullets parsed from findings.json — always an array. */
export function runFindings(
  run: Pick<RunView, 'findings'> | null | undefined
): string[] {
  const list = run?.findings;
  return Array.isArray(list)
    ? list.filter((f) => typeof f === 'string' && f.trim() !== '')
    : [];
}

// ---------- run-history states ----------

/**
 * The empty-history promise line: among ACTIVE agents with a known
 * next_run_at, phrase the soonest one ("first report due Mon 2 Sep, 07:00" —
 * "next report due …" once that agent has run before). '' when no active
 * agent has a scheduled moment yet; the caller shows its own placeholder.
 */
export function nextReportPromise(
  agents: AgentView[],
  now: Date = new Date(),
  t: Translate = englishT
): string {
  const scheduled = (Array.isArray(agents) ? agents : []).filter(
    (a) => a?.is_active && a.next_run_at
  );
  if (scheduled.length === 0) return '';
  const soonest = scheduled.reduce((best, a) => {
    const bestAt = new Date(best.next_run_at as string).getTime();
    const at = new Date(a.next_run_at as string).getTime();
    return Number.isFinite(at) && (!Number.isFinite(bestAt) || at < bestAt)
      ? a
      : best;
  });
  return nextRunPhrase(soonest.next_run_at, !soonest.last_run_at, now, t);
}

export interface RunProblemBanner {
  title: string;
  body: string;
}

/**
 * The plain-language banner above the run history. Keys off the NEWEST run
 * only: a failure that has since been retried (a newer run in flight or
 * ready) needs no alarm. Both problem statuses say what actually happened
 * AND what happens next.
 */
export function latestRunProblem(
  runs: RunView[],
  t: Translate = englishT
): RunProblemBanner | null {
  const latest = Array.isArray(runs) && runs.length > 0 ? runs[0] : null;
  if (!latest) return null;
  if (latest.status === 'failed') {
    const reason = (latest.error ?? '').trim();
    return {
      title: t('AgentRunProblemFailedTitle', 'The last run failed'),
      body: t(
        'AgentRunProblemFailedBody',
        '{{reason}} Nothing was delivered to your client. The agent tries again at its next scheduled run — or start one now with "Run now".',
        {
          reason:
            reason ||
            t('AgentRunProblemNoReport', 'The report could not be generated.'),
        }
      ),
    };
  }
  if (latest.status === 'budget_reached') {
    return {
      title: t('AgentRunProblemBudgetTitle', 'The last run hit its budget cap'),
      body: t(
        'AgentRunProblemBudgetBody',
        'The run stopped before finishing because it reached its per-run budget, so no report was delivered. The agent tries again at its next scheduled run — if this keeps happening, raise the per-run budget for this agent.'
      ),
    };
  }
  return null;
}

/**
 * The full-width error line under a run row. The agent/server error text
 * verbatim when there is one; a plain fallback for failed / budget_reached
 * rows that carry none; '' for healthy rows (render nothing).
 */
export function runRowErrorText(
  run: Pick<RunView, 'status' | 'error'>,
  t: Translate = englishT
): string {
  const err = (run.error ?? '').trim();
  if (err) return err;
  if (run.status === 'failed') {
    return t(
      'AgentRunRowFailed',
      'The run failed before a report was produced.'
    );
  }
  if (run.status === 'budget_reached') {
    return t(
      'AgentRunRowBudget',
      'Stopped at the per-run budget cap before a report was produced.'
    );
  }
  return '';
}

// ---------- agent state chip ----------

export interface AgentStateChip {
  label: string;
  tone: AgentTone;
}

/**
 * The one-glance state pill on each agent card. `needs_attention` outranks
 * everything — a paused-with-error agent must not look green.
 */
export function agentStateChip(
  agent: AgentView | null | undefined,
  t: Translate = englishT
): AgentStateChip {
  const off: AgentStateChip = {
    label: t('AgentStateOff', 'Off'),
    tone: 'neutral',
  };
  if (!agent) return off;
  if (agent.needs_attention) {
    return {
      label: t('AgentStateNeedsAttention', 'Needs attention'),
      tone: 'danger',
    };
  }
  if (agent.is_active) {
    return { label: t('AgentStateActive', 'Active'), tone: 'success' };
  }
  return off;
}

// ---------- run-now enablement ----------

/**
 * Why `POST shops/{slug}/agents/{type}/run` would be refused right now.
 *
 *  - `not_activated` → no agent row for this type (the toggle is off)
 *  - `inactive`      → row exists but is_active false → 422
 *  - `run_in_flight` → a run is already running/generating → 409
 */
export type AgentRunBlocker = 'not_activated' | 'inactive' | 'run_in_flight';

/**
 * What stops a manual run for this agent, or null when the API would accept
 * one. Mirrors the endpoint's own refusals (409 in-flight / 422 inactive) so
 * no control offers an action the API is going to refuse.
 */
export function agentRunBlocker(
  agent: AgentView | null | undefined,
  runs: RunView[]
): AgentRunBlocker | null {
  if (!agent) return 'not_activated';
  if (!agent.is_active) return 'inactive';
  const list = Array.isArray(runs) ? runs : [];
  if (
    list.some(
      (r) => r.agent_type === agent.agent_type && isRunInFlight(r.status)
    )
  ) {
    return 'run_in_flight';
  }
  return null;
}

/**
 * The sentence shown on the disabled "Run now" button (and inline under it) —
 * always naming the actual missing prerequisite, never a generic tooltip.
 */
export function runBlockerMessage(
  blocker: AgentRunBlocker | null,
  typeLabel?: string,
  t: Translate = englishT
): string {
  const agent = typeLabel ?? t('AgentThisAgent', 'this agent');
  switch (blocker) {
    case 'not_activated':
      return t(
        'AgentRunBlockerNotActivated',
        'Switch {{agent}} on first — "Run now" needs an activated agent.',
        { agent }
      );
    case 'inactive':
      return t(
        'AgentRunBlockerInactive',
        'Switch {{agent}} back on first — while it is off, scheduled and manual runs are both refused.',
        { agent }
      );
    case 'run_in_flight':
      return t(
        'AgentRunBlockerInFlight',
        'A run is already in flight for {{agent}} — this page updates on its own as it progresses.',
        { agent }
      );
    default:
      return '';
  }
}

// ---------- money ----------

/**
 * Integer cents → "€4.00". Used for the per-run budget and per-run cost
 * (`effective_budget_cents` / `cost_cents` are integer cents on the wire).
 * null/undefined/NaN ⇒ an em dash.
 */
export function formatCentsEUR(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '—';
  const n = Number(cents);
  if (!Number.isFinite(n)) return '—';
  return `€${(n / 100).toFixed(2)}`;
}

// ---------- dates & periods ----------

const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const dayAbbr = (i: number, t: Translate): string =>
  t(`AgentDayShort${DAY_ABBR[i]}`, DAY_ABBR[i]);
const monthAbbr = (i: number, t: Translate): string =>
  t(`AgentMonthShort${MONTH_ABBR_EN[i]}`, MONTH_ABBR_EN[i]);
const monthName = (i: number, t: Translate): string =>
  t(`AgentMonth${MONTH_NAMES[i]}`, MONTH_NAMES[i]);
const weekdayName = (i: number, t: Translate): string =>
  t(`AgentWeekday${DOW_NAMES_EN[i]}`, DOW_NAMES_EN[i]);
const MONTH_ABBR_EN = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

/** Browser IANA timezone — the schedule-editor default for new agents. */
export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * ISO timestamp → "Mon 2 Sep, 07:00" in the viewer's local time (the year is
 * appended only when it differs from `now`'s). Unparsable input ⇒ ''.
 */
export function formatRunMoment(
  iso: string | null | undefined,
  now: Date = new Date(),
  t: Translate = englishT
): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const base = `${dayAbbr(d.getDay(), t)} ${d.getDate()} ${monthAbbr(
    d.getMonth(),
    t
  )}, ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  return d.getFullYear() === now.getFullYear()
    ? base
    : `${base} ${d.getFullYear()}`;
}

/**
 * The next-run line: "first report due Mon 2 Sep, 07:00" on a brand that has
 * never produced one, "next report due …" afterwards. '' when the moment is
 * unknown — the caller renders its own placeholder instead of a fake date.
 */
export function nextRunPhrase(
  iso: string | null | undefined,
  isFirst = false,
  now: Date = new Date(),
  t: Translate = englishT
): string {
  const moment = formatRunMoment(iso, now, t);
  if (!moment) return '';
  return isFirst
    ? t('AgentFirstReportDue', 'first report due {{moment}}', { moment })
    : t('AgentNextReportDue', 'next report due {{moment}}', { moment });
}

interface ParsedYmd {
  year: number;
  month: number; // 1-based
  day: number;
}

/** Strict `YYYY-MM-DD` parse (no Date/timezone involvement). */
function parseYmd(value: string | null | undefined): ParsedYmd | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((value ?? '').trim());
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

/** `2026-08-18` → `18 Aug 2026`. Unparsable ⇒ ''. */
export function formatDateShort(
  value: string | null | undefined,
  t: Translate = englishT
): string {
  const p = parseYmd(value);
  if (!p) return '';
  return `${p.day} ${monthAbbr(p.month - 1, t)} ${p.year}`;
}

/**
 * Human label for a run's covered period:
 *  - a single day           → "Wed 29 Jul 2026"
 *  - a full calendar month  → "August 2026"
 *  - same-month range       → "18–24 Aug 2026"   (the weekly case)
 *  - cross-month range      → "28 Aug – 3 Sep 2026"
 *  - cross-year range       → "29 Dec 2025 – 4 Jan 2026"
 * One missing end degrades to the other date alone; both missing ⇒ ''.
 */
export function formatPeriodRange(
  start: string | null | undefined,
  end: string | null | undefined,
  t: Translate = englishT
): string {
  const s = parseYmd(start);
  const e = parseYmd(end);
  if (!s && !e) return '';
  if (!s) return formatDateShort(end, t);
  if (!e) return formatDateShort(start, t);

  if (s.year === e.year && s.month === e.month && s.day === e.day) {
    // UTC so the weekday never drifts with the browser's timezone.
    const weekday = new Date(Date.UTC(s.year, s.month - 1, s.day)).getUTCDay();
    return `${dayAbbr(weekday, t)} ${s.day} ${monthAbbr(s.month - 1, t)} ${
      s.year
    }`;
  }
  if (
    s.year === e.year &&
    s.month === e.month &&
    s.day === 1 &&
    e.day === daysInMonth(e.year, e.month)
  ) {
    return `${monthName(s.month - 1, t)} ${s.year}`;
  }
  if (s.year === e.year && s.month === e.month) {
    return `${s.day}–${e.day} ${monthAbbr(s.month - 1, t)} ${s.year}`;
  }
  if (s.year === e.year) {
    return `${s.day} ${monthAbbr(s.month - 1, t)} – ${e.day} ${monthAbbr(
      e.month - 1,
      t
    )} ${s.year}`;
  }
  return `${s.day} ${monthAbbr(s.month - 1, t)} ${s.year} – ${
    e.day
  } ${monthAbbr(e.month - 1, t)} ${e.year}`;
}

/**
 * The period label for a run row: the server's own `period_label` wins, then
 * the derived range, then an em dash (a run that has not resolved its period
 * yet — e.g. one just queued optimistically).
 */
export function runPeriodLabel(
  run: Pick<
    RunView,
    'period_label' | 'period_start' | 'period_end' | 'expected_period'
  >,
  t: Translate = englishT
): string {
  const explicit = (run.period_label ?? '').trim();
  if (explicit) return explicit;
  const recorded = formatPeriodRange(run.period_start, run.period_end, t);
  if (recorded) return recorded;
  // No period recorded yet (the run is still going): say which one to expect,
  // so a wrong period is visible before the run finishes.
  const expected = expectedPeriodText(run.expected_period, t);
  if (expected) {
    return t('AgentRunPeriodExpected', '{{period}} (expected)', {
      period: expected,
    });
  }
  return '—';
}

/** A server-sent expected / run-now period as display text; '' when absent. */
export function expectedPeriodText(
  period:
    | { start?: string | null; end?: string | null; label?: string | null }
    | null
    | undefined,
  t: Translate = englishT
): string {
  if (!period) return '';
  return (
    formatPeriodRange(period.start, period.end, t) ||
    (period.label ?? '').trim()
  );
}

// ---------- cron ----------

const DOW_NAMES_EN = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

/**
 * Characters a single cron field may contain — kept identical to the backend's
 * structural check so anything the API stores round-trips through the form
 * (names like `MON`, ranges, `?`) instead of blocking Save.
 */
const CRON_FIELD_PATTERN = /^[0-9*/,\-A-Za-z?]+$/;

/**
 * Loose shape check for a 5-field cron expression (minute hour day-of-month
 * month day-of-week). Client-side sanity only — the backend stays
 * authoritative and Anthropic's scheduler is what actually parses it.
 */
export function isValidCronShape(cron: string): boolean {
  const parts = (cron ?? '').trim().split(/\s+/);
  if (parts.length !== 5) return false;
  return parts.every((p) => CRON_FIELD_PATTERN.test(p));
}

function isNumeric(value: string): boolean {
  return /^\d+$/.test(value);
}

/** Weekday name by cron day-of-week (0 = Sunday), for the day picker. */
export function weekdayLabel(day: number, t: Translate = englishT): string {
  return weekdayName(((Math.trunc(day) % 7) + 7) % 7, t);
}

/**
 * Describe the common schedules in words ("At 07:00 every Monday."). Anything
 * outside the handled shapes falls back to echoing the expression so the user
 * still sees exactly what is stored.
 */
export function describeCron(cron: string, t: Translate = englishT): string {
  const raw = (cron ?? '').trim();
  const parts = raw.split(/\s+/);
  const invalid = t('AgentCronInvalid', 'Not a valid 5-field cron expression.');
  if (parts.length !== 5 || !isValidCronShape(raw)) {
    return invalid;
  }
  const [min, hour, dom, mon, dow] = parts;
  const custom = t('AgentCronCustom', 'Custom schedule ({{cron}}).', {
    cron: raw,
  });
  if (!isNumeric(min) || !isNumeric(hour)) return custom;
  const minN = Number(min);
  const hourN = Number(hour);
  if (minN > 59 || hourN > 23) return invalid;
  const time = `${pad2(hourN)}:${pad2(minN)}`;
  if (mon !== '*') return custom;

  if (dom !== '*' && dow === '*') {
    if (!isNumeric(dom)) return custom;
    const domN = Number(dom);
    if (domN < 1 || domN > 31) return invalid;
    return t('AgentCronMonthly', 'At {{time}} on day {{day}} of every month.', {
      time,
      day: domN,
    });
  }
  if (dom === '*' && dow !== '*') {
    if (!isNumeric(dow)) return custom;
    const dowN = Number(dow);
    if (dowN > 7) return invalid;
    return t('AgentCronWeekly', 'At {{time}} every {{weekday}}.', {
      time,
      weekday: weekdayName(dowN % 7, t),
    });
  }
  if (dom === '*' && dow === '*') {
    return t('AgentCronDaily', 'At {{time}} every day.', { time });
  }
  return custom;
}

// ---------- schedule picker (Agents v3, D6) ----------

/**
 * A cron expression the picker can represent: one time of day, every day /
 * one weekday / one day of the month. Anything else is a "custom" cron the
 * picker shows verbatim with a reset.
 */
export interface CronSchedulePreset {
  cadence: AgentCadence;
  /** weekly: 0–6 (Sunday–Saturday); monthly: 1–31; daily: ignored (0). */
  day: number;
  hour: number;
  minute: number;
}

export const DEFAULT_CADENCES: AgentCadence[] = ['weekly', 'monthly'];

/** `"37 7 * * 1"` → weekly Monday 07:37; null for anything the picker can't hold. */
export function parseCronSchedule(cron: string): CronSchedulePreset | null {
  const parts = (cron ?? '').trim().split(/\s+/);
  if (parts.length !== 5) return null;
  const [min, hour, dom, mon, dow] = parts;
  if (!isNumeric(min) || !isNumeric(hour) || mon !== '*') return null;
  const minute = Number(min);
  const hourN = Number(hour);
  if (minute > 59 || hourN > 23) return null;
  if (dom === '*' && dow === '*') {
    return { cadence: 'daily', day: 0, hour: hourN, minute };
  }
  if (dom === '*' && isNumeric(dow)) {
    const d = Number(dow);
    if (d > 7) return null;
    return { cadence: 'weekly', day: d % 7, hour: hourN, minute };
  }
  if (dow === '*' && isNumeric(dom)) {
    const d = Number(dom);
    if (d < 1 || d > 31) return null;
    return { cadence: 'monthly', day: d, hour: hourN, minute };
  }
  return null;
}

/** The inverse of {@link parseCronSchedule}. */
export function composeCronSchedule(p: CronSchedulePreset): string {
  const m = Math.min(Math.max(Math.trunc(p.minute), 0), 59);
  const h = Math.min(Math.max(Math.trunc(p.hour), 0), 23);
  if (p.cadence === 'monthly') {
    const d = Math.min(Math.max(Math.trunc(p.day), 1), 31);
    return `${m} ${h} ${d} * *`;
  }
  if (p.cadence === 'weekly') {
    const d = ((Math.trunc(p.day) % 7) + 7) % 7;
    return `${m} ${h} * * ${d}`;
  }
  return `${m} ${h} * * *`;
}

/** 1 → "1st", 22 → "22nd"; the suffixes are translatable ("1e" in Dutch). */
function ordinal(n: number, t: Translate): string {
  const th = `${n}${t('AgentOrdinalSuffixTh', 'th')}`;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return th;
  switch (n % 10) {
    case 1:
      return `${n}${t('AgentOrdinalSuffixSt', 'st')}`;
    case 2:
      return `${n}${t('AgentOrdinalSuffixNd', 'nd')}`;
    case 3:
      return `${n}${t('AgentOrdinalSuffixRd', 'rd')}`;
    default:
      return th;
  }
}

/**
 * The schedule in words for the picker and the cards:
 *   "Every Monday at 07:00" · "On the 1st of every month at 07:00" ·
 *   "Every day at 06:30" · a custom cron falls back to {@link describeCron}.
 * With a timezone: "Every Monday at 07:00 (Europe/Amsterdam)".
 */
export function describeSchedule(
  cron: string,
  timezone?: string,
  t: Translate = englishT
): string {
  const p = parseCronSchedule(cron);
  const tz = timezone ? ` (${timezone})` : '';
  if (!p) return describeCron(cron, t);
  const time = `${pad2(p.hour)}:${pad2(p.minute)}`;
  if (p.cadence === 'weekly') {
    return `${t('AgentScheduleWeekly', 'Every {{weekday}} at {{time}}', {
      weekday: weekdayName(p.day, t),
      time,
    })}${tz}`;
  }
  if (p.cadence === 'monthly') {
    return `${t(
      'AgentScheduleMonthly',
      'On the {{ordinal}} of every month at {{time}}',
      { ordinal: ordinal(p.day, t), time }
    )}${tz}`;
  }
  return `${t('AgentScheduleDaily', 'Every day at {{time}}', { time })}${tz}`;
}
